import { inject, observer } from 'mobx-react';
import React, { Component, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { Link } from 'react-router-dom';

import {
  ButtonListBlock,
  ButtonListBlockBody,
  ButtonListBlockItem
} from 'blocks/ButtonListBlock/ButtonListBlock';
import ItemStackBlock from 'blocks/ItemStackBlock';
import Divider from 'components/Divider/Divider';
import GenericErrorComponent from 'components/InfoBoxComponent/GenericErrorComponent';
import ListStackBlock from 'components/ListStackBlock/ListStackBlock';
import LoadingOverlayComponent from 'components/LoadingOverlayComponent';
import Paragraph from 'components/Paragraph/Paragraph';
import TextElement from 'components/TextElement/TextElement';
import Headline from 'elements/Headline';
import UppercaseHeading from 'elements/UppercaseHeading';
import { PotentialPrintProblemsStoreType } from 'models/PotentialPrintProblemsStore';
import chapterPath from 'utils/chapter-path';
import {
  ROUTE_LAYOUT,
  ROUTE_PDF_PREVIEW,
  ROUTE_SETTINGS
} from 'utils/constants/routes';

interface PotentialPrintProblemsScreenProps {
  link: boolean;
  potentialPrintProblemsStore: PotentialPrintProblemsStoreType;
}

@inject('potentialPrintProblemsStore')
@observer
class PotentialPrintProblems extends Component<
  PotentialPrintProblemsScreenProps
> {
  componentDidMount() {
    this.loadData();
  }

  loadData() {
    this.props.potentialPrintProblemsStore.getPotentialPrintProblems();
  }

  render() {
    const { link } = this.props;

    const MaybeLink = ({
      to,
      children,
      query
    }: {
      to: string;
      children: ReactNode;
      query?: string;
    }) =>
      link ? (
        <Link
          to={{
            pathname: to,
            state: { backRoute: ROUTE_PDF_PREVIEW },
            search: query
          }}
        >
          {children}
        </Link>
      ) : (
        <>{children}</>
      );

    const {
      potentialPrintProblemsStore: { loadingState, potentialPrintProblems }
    } = this.props;
    if (loadingState === 'loading') {
      return <LoadingOverlayComponent />;
    }
    if (loadingState === 'error') {
      return <GenericErrorComponent onRetryClick={() => this.loadData()} />;
    }

    if (!potentialPrintProblems) {
      return null;
    }

    const {
      chapters_not_printed,
      profiles_not_printed_factsheet,
      profiles_not_printed_yearbook,
      profiles_without_groups,
      groups_without_factsheet_chapter,
      groups_without_yearbook_chapter,
      global_factsheet,
      global_yearbook,
      cover_with_placeholders,
      rankings_with_overflow
    } = potentialPrintProblems;

    const hasChapters = chapters_not_printed.length > 0;
    const hasProfilesNotPrintedFactsheet =
      profiles_not_printed_factsheet.length > 0;
    const hasProfilesNotPrintedYearbook =
      profiles_not_printed_yearbook.length > 0;
    const hasProfilesWithoutGroup = profiles_without_groups.length > 0;
    const hasGroupsWithoutFactsheet =
      groups_without_factsheet_chapter.length > 0;
    const hasGroupsWithoutYearbook = groups_without_yearbook_chapter.length > 0;
    const hasRankingsWithOverflow = rankings_with_overflow.length > 0;

    return (
      <ItemStackBlock gap="L">
        <>
          <Divider />
        </>

        {hasChapters && (
          <>
            <section>
              <ItemStackBlock gap="XXS">
                <Headline.Medium>
                  <FormattedMessage id="Chapters not printed" />
                </Headline.Medium>
                <Paragraph>
                  <FormattedMessage id="Chapters not printed check" />
                </Paragraph>
              </ItemStackBlock>
              <ItemStackBlock gap="XXS" marginTop="S">
                <UppercaseHeading>
                  <FormattedMessage
                    id="chapters count"
                    values={{
                      count: chapters_not_printed.length
                    }}
                  />
                </UppercaseHeading>
                <ListStackBlock>
                  <ButtonListBlock>
                    {chapters_not_printed.map((chapter) => (
                      <ButtonListBlockItem key={chapter.id}>
                        <MaybeLink
                          to={
                            chapterPath(chapter.chapter_type, chapter.id) +
                            '/settings'
                          }
                          query="?active=print"
                        >
                          <ButtonListBlockBody arrow={link}>
                            <TextElement color="TEXT_DARK">
                              {chapter.title}
                            </TextElement>
                          </ButtonListBlockBody>
                        </MaybeLink>
                      </ButtonListBlockItem>
                    ))}
                  </ButtonListBlock>
                </ListStackBlock>
              </ItemStackBlock>
              <Divider />
            </section>
          </>
        )}

        {hasRankingsWithOverflow && (
          <section>
            <ItemStackBlock gap="XXS">
              <Headline.Medium>
                <FormattedMessage id="Rankings check" />
              </Headline.Medium>
              <Paragraph>
                <FormattedMessage id="Rankings check info" />
              </Paragraph>
            </ItemStackBlock>
            <ItemStackBlock gap="XXS" marginTop="S">
              <UppercaseHeading>
                <FormattedMessage
                  id="rankings count"
                  values={{
                    count: rankings_with_overflow.length
                  }}
                />
              </UppercaseHeading>

              <ListStackBlock>
                <ButtonListBlock>
                  {rankings_with_overflow.map((ranking) => (
                    <ButtonListBlockItem key={ranking.id}>
                      <MaybeLink
                        to={chapterPath('ranking', ranking.chapter_id)}
                      >
                        <ButtonListBlockBody arrow={link}>
                          <TextElement color="TEXT_DARK">
                            {ranking.name}
                          </TextElement>
                        </ButtonListBlockBody>
                      </MaybeLink>
                    </ButtonListBlockItem>
                  ))}
                </ButtonListBlock>
              </ListStackBlock>
            </ItemStackBlock>

            <Divider />
          </section>
        )}

        {cover_with_placeholders && (
          <section>
            <ItemStackBlock gap="XXS">
              <Headline.Medium>
                <FormattedMessage id="Cover with placeholders" />
              </Headline.Medium>
              <Paragraph>
                <FormattedMessage id="Cover with placeholders info" />
              </Paragraph>
            </ItemStackBlock>
            <MaybeLink to={ROUTE_LAYOUT + '/front'}>
              <ButtonListBlockBody arrow={link}>
                <TextElement color="TEXT_DARK">
                  <FormattedMessage id="Front page" />
                </TextElement>
              </ButtonListBlockBody>
            </MaybeLink>

            <Divider />
          </section>
        )}

        {hasProfilesNotPrintedFactsheet && (
          <section>
            <ItemStackBlock gap="XXS">
              <Headline.Medium>
                <FormattedMessage id="Factsheets not printed" />
              </Headline.Medium>
              <Paragraph>
                <FormattedMessage id="Students not printed check" />
              </Paragraph>
            </ItemStackBlock>
            <ItemStackBlock gap="XXS" marginTop="S">
              <UppercaseHeading>
                <FormattedMessage
                  id="students count"
                  values={{
                    count: profiles_not_printed_factsheet.length
                  }}
                />
              </UppercaseHeading>
              <ListStackBlock>
                <ButtonListBlock>
                  {profiles_not_printed_factsheet.map((profile) => (
                    <ButtonListBlockItem key={profile.id}>
                      <MaybeLink
                        to={`${ROUTE_SETTINGS}/students/${profile.id}/print`}
                      >
                        <ButtonListBlockBody arrow={link}>
                          <TextElement color="TEXT_DARK">
                            {profile.first_name} {profile.last_name}
                          </TextElement>
                        </ButtonListBlockBody>
                      </MaybeLink>
                    </ButtonListBlockItem>
                  ))}
                </ButtonListBlock>
              </ListStackBlock>
            </ItemStackBlock>
            <Divider />
          </section>
        )}

        {!global_factsheet && (
          <>
            {hasProfilesWithoutGroup && (
              <section>
                <ItemStackBlock gap="XXS">
                  <Headline.Medium>
                    <FormattedMessage id="Students without group" />
                  </Headline.Medium>
                  <Paragraph>
                    <FormattedMessage id="Students without group check" />
                  </Paragraph>
                </ItemStackBlock>
                <ItemStackBlock gap="XXS" marginTop="S">
                  <UppercaseHeading>
                    <FormattedMessage
                      id="students count"
                      values={{
                        count: profiles_without_groups.length
                      }}
                    />
                  </UppercaseHeading>
                  <ListStackBlock>
                    <ButtonListBlock>
                      {profiles_without_groups.map((profile) => (
                        <ButtonListBlockItem key={profile.id}>
                          <MaybeLink
                            to={`${ROUTE_SETTINGS}/students/${profile.id}/groups`}
                          >
                            <ButtonListBlockBody arrow={link}>
                              <TextElement color="TEXT_DARK">
                                {profile.first_name} {profile.last_name}
                              </TextElement>
                            </ButtonListBlockBody>
                          </MaybeLink>
                        </ButtonListBlockItem>
                      ))}
                    </ButtonListBlock>
                  </ListStackBlock>
                </ItemStackBlock>
                <Divider />
              </section>
            )}

            {hasGroupsWithoutFactsheet && (
              <section>
                <ItemStackBlock gap="XXS">
                  <Headline.Medium>
                    <FormattedMessage id="Groups without factsheet chapter" />
                  </Headline.Medium>
                  <Paragraph>
                    <FormattedMessage id="Groups without factsheet chapter check" />
                  </Paragraph>
                </ItemStackBlock>
                <ItemStackBlock gap="XXS" marginTop="S">
                  <UppercaseHeading>
                    <FormattedMessage
                      id="groups count"
                      values={{
                        count: groups_without_factsheet_chapter.length
                      }}
                    />
                  </UppercaseHeading>
                  <ListStackBlock>
                    <ButtonListBlock>
                      {groups_without_factsheet_chapter.map((group) => (
                        <ButtonListBlockItem key={group.id}>
                          <MaybeLink
                            to={`${ROUTE_SETTINGS}/groups/${group.id}`}
                          >
                            <ButtonListBlockBody arrow={link}>
                              <TextElement color="TEXT_DARK">
                                {group.name}
                              </TextElement>
                            </ButtonListBlockBody>
                          </MaybeLink>
                        </ButtonListBlockItem>
                      ))}
                    </ButtonListBlock>
                  </ListStackBlock>
                </ItemStackBlock>
                <Divider />
              </section>
            )}
          </>
        )}

        {hasProfilesNotPrintedYearbook && (
          <section>
            <ItemStackBlock gap="XXS">
              <Headline.Medium>
                <FormattedMessage id="Yearbooks not printed" />
              </Headline.Medium>
              <Paragraph>
                <FormattedMessage id="Students yearbook not printed check" />
              </Paragraph>
            </ItemStackBlock>
            <ItemStackBlock gap="XXS" marginTop="S">
              <UppercaseHeading>
                <FormattedMessage
                  id="students count"
                  values={{
                    count: profiles_not_printed_yearbook.length
                  }}
                />
              </UppercaseHeading>
              <ListStackBlock>
                <ButtonListBlock>
                  {profiles_not_printed_yearbook.map((profile) => (
                    <ButtonListBlockItem key={profile.id}>
                      <MaybeLink
                        to={`${ROUTE_SETTINGS}/students/${profile.id}/print`}
                      >
                        <ButtonListBlockBody arrow={link}>
                          <TextElement color="TEXT_DARK">
                            {profile.first_name} {profile.last_name}
                          </TextElement>
                        </ButtonListBlockBody>
                      </MaybeLink>
                    </ButtonListBlockItem>
                  ))}
                </ButtonListBlock>
              </ListStackBlock>
            </ItemStackBlock>
            <Divider />
          </section>
        )}

        {!global_yearbook && (
          <>
            {hasProfilesWithoutGroup && (
              <section>
                <ItemStackBlock gap="XXS">
                  <Headline.Medium>
                    <FormattedMessage id="Students without group" />
                  </Headline.Medium>
                  <Paragraph>
                    <FormattedMessage id="Students without group check yearbook" />
                  </Paragraph>
                </ItemStackBlock>
                <ItemStackBlock gap="XXS" marginTop="S">
                  <UppercaseHeading>
                    <FormattedMessage
                      id="students count"
                      values={{
                        count: profiles_without_groups.length
                      }}
                    />
                  </UppercaseHeading>
                  <ListStackBlock>
                    <ButtonListBlock>
                      {profiles_without_groups.map((profile) => (
                        <ButtonListBlockItem key={profile.id}>
                          <MaybeLink
                            to={`${ROUTE_SETTINGS}/students/${profile.id}/group`}
                          >
                            <ButtonListBlockBody arrow={link}>
                              <TextElement color="TEXT_DARK">
                                {profile.first_name} {profile.last_name}
                              </TextElement>
                            </ButtonListBlockBody>
                          </MaybeLink>
                        </ButtonListBlockItem>
                      ))}
                    </ButtonListBlock>
                  </ListStackBlock>
                </ItemStackBlock>
                <Divider />
              </section>
            )}

            {hasGroupsWithoutYearbook && (
              <section>
                <ItemStackBlock gap="XXS">
                  <Headline.Medium>
                    <FormattedMessage id="Groups without yearbook chapter" />
                  </Headline.Medium>
                  <Paragraph>
                    <FormattedMessage id="Groups without yearbook chapter check" />
                  </Paragraph>
                </ItemStackBlock>
                <ItemStackBlock gap="XXS" marginTop="S">
                  <UppercaseHeading>
                    <FormattedMessage
                      id="groups count"
                      values={{
                        count: groups_without_yearbook_chapter.length
                      }}
                    />
                  </UppercaseHeading>
                  <ListStackBlock>
                    <ButtonListBlock>
                      {groups_without_yearbook_chapter.map((group) => (
                        <ButtonListBlockItem key={group.id}>
                          <MaybeLink
                            to={`${ROUTE_SETTINGS}/groups/${group.id}`}
                          >
                            <ButtonListBlockBody arrow={link}>
                              <TextElement color="TEXT_DARK">
                                {group.name}
                              </TextElement>
                            </ButtonListBlockBody>
                          </MaybeLink>
                        </ButtonListBlockItem>
                      ))}
                    </ButtonListBlock>
                  </ListStackBlock>
                </ItemStackBlock>
                <Divider />
              </section>
            )}
          </>
        )}
      </ItemStackBlock>
    );
  }
}

export default (props: any) => (
  // @ts-ignore
  <PotentialPrintProblems {...props} />
);
